import { NodeMap, PageData, PartialData } from '@app-velocity/common';

import {
  ConvertedImageBase64,
  ConvertedImageRaw,
} from '../../../../../../../core/types';

import { bufferToBase64 } from '../../../../../../utils/image';
import { ChallengeSaveError } from '../../../../../../../types';
import {
  getVariantNodeImage,
  getSimulationPageImage,
  getPartialNodeImage,
} from '../../../../../../../core';
import { isChallengeSaveError } from '../../../../../../../guards/isChallengeSaveError';

export const useGeneratePage = () => {
  const getActionsFromNodesMap = (nodesMap: NodeMap) => {
    const nodes = Object.values(nodesMap);

    return nodes
      .filter((nodes) => {
        return !!nodes.actions && nodes.actions.length > 0;
      })
      .map((node) => {
        const allActions = node.actions || [];

        const filteredActions = allActions.filter((action) => {
          if (action.action === 'change') {
            const changeTo = action.to;
            const changeFrom = action.from;

            if (!changeTo || !changeFrom) return false;

            const normalizedChangeFrom = changeFrom.replace(/[:;]/g, '-');

            const nodeToChangeFrom = nodesMap[normalizedChangeFrom];
            if (!normalizedChangeFrom) return false;
            const mainInstance = nodeToChangeFrom.variants?.find(
              (variant) => variant.instanceId === changeFrom
            );
            if (!mainInstance) return false;
            const nodeToChangeTo = mainInstance.variants.find(
              (variant) => variant.id === changeTo
            );
            if (!nodeToChangeTo) return false;

            // if next variant has any action
            if ((nodeToChangeTo.actions || []).length > 0) return true;

            // if next variant has some properties to set
            if (nodeToChangeTo.properties.length > 0) return true;

            // if text content changed
            if (nodeToChangeFrom.characters !== nodeToChangeTo.characters)
              return true;

            return false;
          }

          return true;
        });

        return filteredActions;
      })
      .flat();
  };

  /**
   *
   * @returns generated image and data or error
   */
  const generatePage = async (
    nodeId: string,
    nodesMap: NodeMap
  ): Promise<
    | ChallengeSaveError
    | {
        image: ConvertedImageBase64;
        data: PageData | PartialData;
      }
  > => {
    const imageResponse = await getSimulationPageImage(nodeId);

    if (isChallengeSaveError(imageResponse)) {
      return imageResponse;
    }

    if (!imageResponse) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const nodeData = nodesMap[nodeId.replace(/[:;]/g, '-')];

    if (!nodeData) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const base64Image = imageResponse.converted
      ? await bufferToBase64(imageResponse.converted)
      : '';

    imageResponse.converted = null;

    return {
      image: {
        converted: base64Image,
      },
      data: {
        ...nodeData,
        width: nodeData.width,
        height: nodeData.height,
        actions: getActionsFromNodesMap(nodesMap),
      },
    };
  };

  /**
   *
   * @returns generated image and data or error
   */
  const generatePartial = async (
    nodeId: string,
    nodesMap: NodeMap
  ): Promise<
    | ChallengeSaveError
    | {
        image: ConvertedImageBase64;
        data: PageData | PartialData;
      }
  > => {
    const promises: [Promise<ConvertedImageRaw | ChallengeSaveError | null>] = [
      getPartialNodeImage(nodeId),
    ];

    const res = await Promise.all(promises);

    const [imageResponse] = res;

    if (isChallengeSaveError(imageResponse)) {
      return imageResponse;
    }

    if (!imageResponse) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const nodeData = nodesMap[nodeId.replace(/[:;]/g, '-')];

    if (!nodeData) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const base64Image = imageResponse.converted
      ? await bufferToBase64(imageResponse.converted)
      : '';

    imageResponse.converted = null;

    return {
      image: {
        converted: base64Image,
      },
      data: {
        ...nodeData,
        actions: getActionsFromNodesMap(nodesMap),
      },
    };
  };

  /**
   *
   * @returns generated image and data or error
   */
  const generateVariant = async (
    nodeId: string,
    parentNodeId: string,
    nodesMap: NodeMap
  ): Promise<
    | ChallengeSaveError
    | {
        image: ConvertedImageBase64;
        data: PageData | PartialData;
      }
  > => {
    const promises: [Promise<ConvertedImageRaw | ChallengeSaveError | null>] = [
      getVariantNodeImage(nodeId, parentNodeId),
    ];

    const res = await Promise.all(promises);

    const [imageResponse] = res;

    if (isChallengeSaveError(imageResponse)) {
      return imageResponse;
    }

    if (!imageResponse) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const nodeData = nodesMap[nodeId.replace(/[:;]/g, '-')];

    if (!nodeData) {
      return { type: 'NODE_NOT_FOUND', nodeId };
    }

    const base64Image = imageResponse.converted
      ? await bufferToBase64(imageResponse.converted)
      : '';

    imageResponse.converted = null;

    return {
      image: {
        converted: base64Image,
      },
      data: {
        ...nodeData,
        actions: getActionsFromNodesMap(nodesMap),
      },
    };
  };

  return {
    generatePage,
    generatePartial,
    generateVariant,
  };
};
